import type { ComponentProps } from 'react';
import React, { useCallback } from 'react';
import { Pressable } from 'react-native';
import {
  Chip,
  theme,
  useNoticeBannersStackContext,
  useResponsive,
  WithTooltip,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

// Garnish <Chip /> with a tooltip/banner trigger wrapper.
export const TooltipChip = (
  props: ComponentProps<typeof Chip> & { readonly showTooltip?: boolean },
) => {
  const { currentBreakpoint } = useResponsive();
  const { t } = useLocalizationContext();
  const label = t('addresses.error.name.duplicate');

  // mobile view uses a notice banner instead of a tooltip.
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const showNoticeBanner = useCallback(() => {
    addNoticeBanner({ text: label, palette: 'caution' });
  }, [addNoticeBanner, label]);

  // should not show tooltip by prop override.
  if (!props.showTooltip) {
    return <Chip {...props}>{props.children}</Chip>;
  }

  // should show banner trigger on mobile.
  if (currentBreakpoint.isXS) {
    return (
      <Pressable
        onPress={showNoticeBanner}
        accessibilityRole="button"
        accessibilityLabel={label}
      >
        <Chip disabled {...props}>
          {props.children}
        </Chip>
      </Pressable>
    );
  }

  // should show tooltip on desktop.
  return (
    <Chip disabled {...props}>
      <WithTooltip
        text={label}
        placement="top"
        triggerMode="hover"
        offsetY={-theme.spacing['1']}
        toggleAccessibilityLabel={label}
      >
        {props.children}
      </WithTooltip>
    </Chip>
  );
};
